import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Built-in tester for a Tool flow: fills the input params and runs the flow on
// the backend (POST /run-tool), showing the value the AI would receive.
const useStyles = makeStyles(theme => ({
  hint: { fontSize: 12.5, color: theme.app.textSecondary, marginBottom: 12 },
  field: { marginBottom: 12 },
  resultBox: {
    marginTop: 8,
    border: `1px solid ${theme.app.border}`,
    borderRadius: 10,
    padding: 12,
    background: "#FAFAF7",
  },
  resultLabel: { fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: theme.app.textTertiary, marginBottom: 6 },
  pre: { margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace", fontSize: 12.5, color: theme.app.ink },
  ok: { color: "#1E7E34", fontWeight: 600 },
  err: { color: "#C92A2A", fontWeight: 600 },
  desc: { fontSize: 12, color: theme.app.textSecondary, marginTop: 8 },
  warn: { fontSize: 12.5, color: "#8a6d00", lineHeight: 1.5, "& code": { fontFamily: "monospace", background: "#F0EDE0", padding: "1px 4px", borderRadius: 4 } },
}));

const fmt = v => {
  if (v == null) return "(vazio)";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const ToolTesterModal = ({ open, onClose, params, ensureSaved, onRan }) => {
  const classes = useStyles();
  const list = Array.isArray(params) ? params.filter(p => p && p.name) : [];
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (name, v) => setVals(s => ({ ...s, [name]: v }));

  const coerce = p => {
    const raw = vals[p.name];
    if (p.type === "number") return raw === "" || raw == null ? undefined : Number(raw);
    if (p.type === "boolean") return raw === "true" || raw === true;
    return raw == null ? "" : raw;
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { id, definition } = await ensureSaved();
      const args = {};
      list.forEach(p => {
        args[p.name] = coerce(p);
      });
      const { data } = await api.post(`/applications/${id}/run-tool`, { args, definition });
      setResult(data);
      // realimenta o picker de variáveis do builder com a saída real dos nós
      if (onRan) onRan(data);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Testar ferramenta</DialogTitle>
      <DialogContent>
        <Typography className={classes.hint}>
          Preencha os parâmetros e execute. O fluxo roda no servidor e mostramos o valor que a IA receberia.
        </Typography>

        {list.length === 0 && (
          <Typography className={classes.hint}>Esta ferramenta não tem parâmetros — é só executar.</Typography>
        )}

        {list.map(p =>
          p.type === "boolean" ? (
            <TextField
              key={p.name}
              className={classes.field}
              label={p.name}
              helperText={p.description}
              select
              value={vals[p.name] != null ? String(vals[p.name]) : "false"}
              onChange={e => set(p.name, e.target.value)}
              size="small"
              variant="outlined"
              fullWidth
            >
              <MenuItem value="true">Sim</MenuItem>
              <MenuItem value="false">Não</MenuItem>
            </TextField>
          ) : (
            <TextField
              key={p.name}
              className={classes.field}
              label={p.name + (p.required !== false ? " *" : "")}
              helperText={p.description}
              type={p.type === "number" ? "number" : "text"}
              value={vals[p.name] != null ? vals[p.name] : ""}
              onChange={e => set(p.name, e.target.value)}
              size="small"
              variant="outlined"
              fullWidth
            />
          )
        )}

        {result && (
          <div className={classes.resultBox}>
            <div className={classes.resultLabel}>
              Retorno{" "}
              <span className={result.ok ? classes.ok : classes.err}>
                {result.ok ? "· ok" : "· erro"}
              </span>
            </div>
            {/* ok mas sem nó Retornar (ou vazio): a IA não recebe nada útil */}
            {result.ok && result.returned === false ? (
              <Typography className={classes.warn}>
                O fluxo executou, mas não há um nó <b>Retornar</b> — então a IA não recebe nenhum valor.
                Adicione um nó <b>Retornar</b> e escolha o dado a devolver (ex.: <code>{"{{steps.…}}"}</code>).
                As variáveis dos nós que rodaram agora aparecem no seletor de variáveis.
              </Typography>
            ) : result.ok && (result.value === "" || result.value == null) ? (
              <Typography className={classes.warn}>
                O nó <b>Retornar</b> devolveu vazio. Confira o valor configurado nele — as variáveis já
                estão disponíveis no seletor.
              </Typography>
            ) : (
              <pre className={classes.pre}>{result.ok ? fmt(result.value) : result.error || "Falha"}</pre>
            )}
            {result.description && <div className={classes.desc}>{result.description}</div>}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button onClick={run} variant="contained" color="secondary" disabled={busy} startIcon={busy ? <CircularProgress size={15} /> : null}>
          {busy ? "Executando…" : "Executar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToolTesterModal;
